import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, Smartphone, KeyRound, Mail, MessageSquare, Trash2, Download } from "lucide-react";

type Method = "totp" | "email" | "sms" | "passkey";

interface MfaStatusResp {
  status: {
    enabled: boolean;
    enrolledMethods: Method[];
    defaultMethod: Method | null;
    passkeysCount: number;
    backupCodesRemaining: number;
  };
  policy: {
    required: boolean;
    requiredEventually: boolean;
    graceUntil: string | null;
    allowedMethods: Method[];
    minStrength: "any" | "no_sms" | "passkey_or_totp";
  };
  passkeys: { id: string; deviceName: string; createdAt: string; lastUsedAt: string | null }[];
  verified: boolean;
  verifiedAt: number | null;
}

export default function AccountSecurityPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<MfaStatusResp>({ queryKey: ["/api/security/mfa/status"] });

  const [totpQr, setTotpQr] = useState<string | null>(null);
  const [totpToken, setTotpToken] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [smsPhone, setSmsPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [passkeyName, setPasskeyName] = useState("");
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["/api/security/mfa/status"] });

  const beginTotp = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/totp", {})).json(),
    onSuccess: (d: any) => setTotpQr(d.qrDataUrl),
    onError: (e: any) => toast({ title: "Could not start setup", description: e.message, variant: "destructive" }),
  });

  const verifyTotp = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/totp/verify", { token: totpToken })).json(),
    onSuccess: (d: any) => {
      toast({ title: "Authenticator app activated" });
      setTotpQr(null);
      setTotpToken("");
      if (Array.isArray(d?.backupCodes) && d.backupCodes.length) setNewBackupCodes(d.backupCodes);
      refresh();
    },
    onError: (e: any) => toast({ title: "Verification failed", description: e.message, variant: "destructive" }),
  });

  const startEmail = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/email/start", {})).json(),
    onSuccess: () => toast({ title: "Code sent to your email" }),
    onError: (e: any) => toast({ title: "Send failed", description: e.message, variant: "destructive" }),
  });

  const verifyEmail = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/email/verify", { code: emailCode })).json(),
    onSuccess: (d: any) => {
      toast({ title: "Email factor activated" });
      setEmailCode("");
      if (Array.isArray(d?.backupCodes) && d.backupCodes.length) setNewBackupCodes(d.backupCodes);
      refresh();
    },
    onError: (e: any) => toast({ title: "Verification failed", description: e.message, variant: "destructive" }),
  });

  const startSms = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/sms/start", { phoneE164: smsPhone })).json(),
    onSuccess: () => toast({ title: "Code sent" }),
    onError: (e: any) => toast({ title: "Send failed", description: e.message, variant: "destructive" }),
  });

  const verifySms = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/enroll/sms/verify", { code: smsCode, phoneE164: smsPhone })).json(),
    onSuccess: (d: any) => {
      toast({ title: "SMS factor activated" });
      setSmsCode("");
      if (Array.isArray(d?.backupCodes) && d.backupCodes.length) setNewBackupCodes(d.backupCodes);
      refresh();
    },
    onError: (e: any) => toast({ title: "Verification failed", description: e.message, variant: "destructive" }),
  });

  const removeFactor = useMutation({
    mutationFn: async (method: Method) => (await apiRequest("DELETE", `/api/security/mfa/method/${method}`)).json(),
    onSuccess: (_, method) => {
      toast({ title: `${method.toUpperCase()} factor removed` });
      refresh();
    },
    onError: (e: any) => toast({ title: "Remove failed", description: e.message, variant: "destructive" }),
  });

  const startPasskey = useMutation({
    mutationFn: async () => {
      const opts = await (await apiRequest("POST", "/api/security/mfa/enroll/passkey/options", {})).json();
      const { startRegistration } = await import("@simplewebauthn/browser");
      const response = await startRegistration({ optionsJSON: opts });
      return (await apiRequest("POST", "/api/security/mfa/enroll/passkey/verify", {
        label: passkeyName || "Passkey",
        response,
      })).json();
    },
    onSuccess: (d: any) => {
      toast({ title: "Passkey added" });
      setPasskeyName("");
      if (Array.isArray(d?.backupCodes) && d.backupCodes.length) setNewBackupCodes(d.backupCodes);
      refresh();
    },
    onError: (e: any) => toast({ title: "Passkey enrollment failed", description: e.message, variant: "destructive" }),
  });

  const removePasskey = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/security/mfa/method/passkey:${id}`)).json(),
    onSuccess: () => {
      toast({ title: "Passkey removed" });
      refresh();
    },
  });

  const regenBackup = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/security/mfa/backup-codes/regenerate", {})).json(),
    onSuccess: (d: { codes: string[] }) => {
      setNewBackupCodes(d.codes);
      refresh();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { status, policy, passkeys } = data;
  const totpEnrolled = status.enrolledMethods.includes("totp");
  const emailEnrolled = status.enrolledMethods.includes("email");
  const smsEnrolled = status.enrolledMethods.includes("sms");

  const downloadCodes = () => {
    if (!newBackupCodes) return;
    const blob = new Blob([newBackupCodes.join("\n") + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "entertain-crm-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const allowed = (m: Method) => policy.allowedMethods.includes(m);

  return (
    <div className="container max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-7 h-7" /> Security
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Add a second factor so a stolen password alone can't unlock your account.
        </p>
        {policy.required && !status.enabled && (
          <Badge variant="destructive" className="mt-2">MFA required by your workspace</Badge>
        )}
        {policy.requiredEventually && !policy.required && (
          <Badge variant="secondary" className="mt-2">
            Required after {policy.graceUntil ? new Date(policy.graceUntil).toLocaleDateString() : "your grace period"}
          </Badge>
        )}
      </div>

      {newBackupCodes && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-base">Backup codes — save these now</CardTitle>
            <CardDescription>One-time codes for emergency access. They will not be shown again.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <pre className="text-sm font-mono whitespace-pre-wrap bg-muted/30 p-3 rounded">{newBackupCodes.join("\n")}</pre>
            <Button variant="outline" size="sm" onClick={downloadCodes} data-testid="button-download-backup-codes">
              <Download className="w-4 h-4 mr-1" /> Download as .txt
            </Button>
          </CardContent>
        </Card>
      )}

      {allowed("totp") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Smartphone className="w-4 h-4" /> Authenticator app (TOTP)
              {totpEnrolled && <Badge variant="default" className="ml-2">Active</Badge>}
            </CardTitle>
            <CardDescription>Scan a QR code with Google Authenticator, 1Password, Authy, or similar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {totpEnrolled ? (
              <Button variant="outline" onClick={() => removeFactor.mutate("totp")} disabled={removeFactor.isPending} data-testid="button-remove-totp">
                <Trash2 className="w-4 h-4 mr-1" /> Remove authenticator
              </Button>
            ) : !totpQr ? (
              <Button onClick={() => beginTotp.mutate()} disabled={beginTotp.isPending} data-testid="button-setup-totp">
                {beginTotp.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Set up authenticator
              </Button>
            ) : (
              <div className="space-y-3">
                <img src={totpQr} alt="TOTP QR" className="w-48 h-48 bg-white p-2 rounded border" data-testid="img-totp-qr" />
                <div>
                  <Label htmlFor="totp-token">6-digit code</Label>
                  <Input id="totp-token" value={totpToken} onChange={(e) => setTotpToken(e.target.value)} className="mt-1 max-w-[160px]" data-testid="input-totp-token" />
                </div>
                <Button onClick={() => verifyTotp.mutate()} disabled={verifyTotp.isPending || totpToken.length < 6} data-testid="button-verify-totp">
                  {verifyTotp.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Verify and activate
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {allowed("passkey") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="w-4 h-4" /> Passkeys / security keys
            </CardTitle>
            <CardDescription>The strongest factor — phishing-resistant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {passkeys.length > 0 && (
              <div className="space-y-2">
                {passkeys.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border rounded-md p-2">
                    <div>
                      <p className="text-sm font-medium" data-testid={`text-passkey-${p.id}`}>{p.deviceName}</p>
                      <p className="text-xs text-muted-foreground">Added {new Date(p.createdAt).toLocaleDateString()}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removePasskey.mutate(p.id)} data-testid={`button-remove-passkey-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input placeholder="Nickname (e.g. Work laptop)" value={passkeyName} onChange={(e) => setPasskeyName(e.target.value)} data-testid="input-passkey-nickname" />
              <Button onClick={() => startPasskey.mutate()} disabled={startPasskey.isPending} data-testid="button-add-passkey">
                {startPasskey.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Add passkey
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {allowed("email") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4" /> Email codes
              {emailEnrolled && <Badge variant="default" className="ml-2">Active</Badge>}
            </CardTitle>
            <CardDescription>One-time codes sent to your account email.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {emailEnrolled ? (
              <Button variant="outline" onClick={() => removeFactor.mutate("email")} data-testid="button-remove-email">
                <Trash2 className="w-4 h-4 mr-1" /> Remove email factor
              </Button>
            ) : (
              <>
                <Button onClick={() => startEmail.mutate()} disabled={startEmail.isPending} data-testid="button-send-email-code">
                  Send verification code
                </Button>
                <div className="flex gap-2 max-w-sm">
                  <Input placeholder="6-digit code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} data-testid="input-email-code" />
                  <Button onClick={() => verifyEmail.mutate()} disabled={verifyEmail.isPending || emailCode.length < 6} data-testid="button-verify-email-code">Verify</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {allowed("sms") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4" /> SMS codes
              {smsEnrolled && <Badge variant="default" className="ml-2">Active</Badge>}
            </CardTitle>
            <CardDescription>
              Less secure than the options above. Your admin may require a stronger factor too.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {smsEnrolled ? (
              <Button variant="outline" onClick={() => removeFactor.mutate("sms")} data-testid="button-remove-sms">
                <Trash2 className="w-4 h-4 mr-1" /> Remove SMS factor
              </Button>
            ) : (
              <>
                <div className="flex gap-2 max-w-sm">
                  <Input placeholder="+15551234567" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} data-testid="input-sms-phone" />
                  <Button onClick={() => startSms.mutate()} disabled={startSms.isPending || smsPhone.length < 7} data-testid="button-send-sms-code">Send code</Button>
                </div>
                <div className="flex gap-2 max-w-sm">
                  <Input placeholder="6-digit code" value={smsCode} onChange={(e) => setSmsCode(e.target.value)} data-testid="input-sms-code" />
                  <Button onClick={() => verifySms.mutate()} disabled={verifySms.isPending || smsCode.length < 6} data-testid="button-verify-sms-code">Verify</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Backup codes</CardTitle>
          <CardDescription>
            One-time codes that work if you lose access to every other factor.{" "}
            {status.backupCodesRemaining > 0 && <span>{status.backupCodesRemaining} unused remaining.</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => regenBackup.mutate()} disabled={regenBackup.isPending} data-testid="button-regen-backup-codes">
            {regenBackup.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {status.backupCodesRemaining > 0 ? "Regenerate backup codes" : "Generate backup codes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
